import { filterObjectValues } from 'helpers/filter'

import { Branch } from 'models/branch'
import { Logistic, LogisticAct } from 'models/logistic'
import { PaginationResponse } from 'models/pagination'
import { IStatistic } from 'models/statistic'

import { downloadFileHandler } from 'shared/libs'

import api, { CreateLogisticTagType } from '../api'
import {
  AssignConsultantParams,
  AssignableConsultantsObject,
  CreateLogisticParams,
  Deliverymen,
  DenyLogistic,
  EditLogisticParams,
  GetExecutorsResponse,
  GetLogisticParams,
  LogisticReportType,
  LogisticShortObject,
  ProcessedLogisticObject,
  RepeatLogisticParams,
  UpdateLogisticStatus,
} from './types'

export const logisticApi = api.injectEndpoints({
  endpoints: build => ({
    getNewLogisticCarts: build.query<
      PaginationResponse<LogisticShortObject>,
      GetLogisticParams
    >({
      query: params => ({
        url: `/web/logistic/new_cart/`,
        params: filterObjectValues(params),
        cache: 'no-store',
      }),
      providesTags: [CreateLogisticTagType],
    }),
    getLogisticCartForEdit: build.query<LogisticShortObject, { id: number }>({
      query: params => ({
        url: `/web/logistic/${params.id}/for_edit/`,
        cache: 'no-store',
      }),
      providesTags: [CreateLogisticTagType],
    }),
    getLogisticBranches: build.query<Array<Branch>, void>({
      query: () => ({
        url: `/web/logistic/resource/branch/`,
      }),
    }),
    getProcessedLogisticCarts: build.query<
      PaginationResponse<ProcessedLogisticObject>,
      GetLogisticParams
    >({
      query: params => ({
        url: `/web/logistic/processed_applications/`,
        params: filterObjectValues(params),
        cache: 'no-store',
      }),
      providesTags: [CreateLogisticTagType],
    }),
    getBranchesApplications: build.query<
      PaginationResponse<LogisticShortObject>,
      GetLogisticParams
    >({
      query: params => ({
        url: `/web/logistic/applications_branches/`,
        params: filterObjectValues(params),
        cache: 'no-store',
      }),
      providesTags: [CreateLogisticTagType],
    }),
    getBranchesActs: build.query<
      PaginationResponse<LogisticAct>,
      GetLogisticParams
    >({
      query: params => ({
        url: `/web/logistic_acts_branches/`,
        params: filterObjectValues(params),
        cache: 'no-store',
      }),
      providesTags: [CreateLogisticTagType],
    }),
    createLogistic: build.mutation<Logistic, CreateLogisticParams>({
      query: body => ({
        method: 'post',
        url: '/web/logistic/',
        body,
      }),
      invalidatesTags: [CreateLogisticTagType],
    }),
    editLogistic: build.mutation<
      Logistic,
      { id: number; body: EditLogisticParams }
    >({
      query: ({ id, body }) => ({
        method: 'put',
        url: `/web/logistic/${id}/edit/`,
        body,
        cache: 'no-cache',
      }),
      invalidatesTags: [CreateLogisticTagType],
    }),
    repeatLogistic: build.mutation<
      Logistic,
      { id: number; body: RepeatLogisticParams }
    >({
      query: ({ id, body }) => ({
        method: 'PATCH',
        url: `/web/logistic/${id}/`,
        body,
      }),
      invalidatesTags: [CreateLogisticTagType],
    }),
    searchAssignableConsultants: build.mutation<
      AssignableConsultantsObject[],
      string
    >({
      query: search => ({
        method: 'get',
        url: `/web/logistic/assign_consultant/`,
        params: { search },
      }),
      invalidatesTags: [CreateLogisticTagType],
    }),
    assignConsultant: build.mutation<Logistic, AssignConsultantParams>({
      query: body => ({
        method: 'POST',
        url: `/web/logistic/assign_consultant/`,
        body,
      }),
      invalidatesTags: [CreateLogisticTagType],
    }),
    updateLogisticStatus: build.mutation<void, UpdateLogisticStatus>({
      query: ({ id, body }) => ({
        method: 'post',
        url: `/web/logistic/${id}/update_status/`,
        body,
      }),
      invalidatesTags: [CreateLogisticTagType],
    }),
    getActByBarcode: build.mutation<LogisticAct[], string>({
      query: search => ({
        method: 'GET',
        url: `/web/logistic_acts_branches/assign_consultant/`,
        params: { search },
      }),
      invalidatesTags: [CreateLogisticTagType],
    }),
    assignConsultantToAct: build.mutation<Logistic, AssignConsultantParams>({
      query: body => ({
        method: 'POST',
        url: `/web/logistic_acts_branches/assign_consultant/`,
        body,
      }),
      invalidatesTags: [CreateLogisticTagType],
    }),
    getLogisticById: build.query<Logistic, Logistic['id']>({
      query: id => ({
        url: `/web/logistic/${id}/`,
      }),
      providesTags: [CreateLogisticTagType],
    }),
    getDeliverymens: build.query<Array<Deliverymen>, void>({
      query: () => ({
        url: `/web/logistic/resource/deliverymen/`,
      }),
    }),
    logisticStatistics: build.query<
      Array<IStatistic>,
      GetLogisticParams & { path_name?: string }
    >({
      query: params => ({
        url: `/web/logistic/statistics/`,
        params: filterObjectValues(params),
      }),
      keepUnusedDataFor: 60,
    }),
    logisticActsStatistics: build.query<Array<IStatistic>, GetLogisticParams>({
      query: params => ({
        url: `/web/logistic_acts_branches/statistics/`,
        params: filterObjectValues(params),
      }),
      keepUnusedDataFor: 60,
    }),
    downloadLogisticActsExcel: build.mutation<void, GetLogisticParams>({
      query: params => ({
        url: `/web/logistic_acts_branches/excel/`,
        params: filterObjectValues(params),
        responseHandler: downloadFileHandler,
        cache: 'no-cache',
      }),
    }),
    getProjects: build.query<Array<{ id: number; name: string }>, void>({
      query: () => ({
        url: `/web/logistic/resource/progects/`,
      }),
    }),
    getCardTypes: build.query<Array<{ id: number; name: string }>, void>({
      query: () => ({
        url: `/web/logistic/resource/card_type/`,
      }),
    }),
    getExecutors: build.query<Array<GetExecutorsResponse>, void>({
      query: () => ({
        url: `/web/logistic/resource/executor/`,
      }),
    }),
    denyLogistic: build.mutation<void, DenyLogistic>({
      query: ({ id, body }) => ({
        method: 'PATCH',
        url: `/web/logistic/${id}/`,
        body,
      }),
      invalidatesTags: [CreateLogisticTagType],
    }),
    downloadLogisticExcel: build.mutation<
      void,
      { params: GetLogisticParams; type?: LogisticReportType }
    >({
      query: ({ params, type = LogisticReportType.DEFAULT }) => ({
        url: `/web/logistic/${type}/`,
        params: filterObjectValues(params),
        responseHandler: downloadFileHandler,
        cache: 'no-cache',
      }),
    }),
    updateFile: build.mutation<void, FormData>({
      query: formData => ({
        method: 'PUT',
        url: `/web/logistic/update_file/`,
        body: formData,
      }),
      invalidatesTags: [CreateLogisticTagType],
    }),
    generateExcel: build.mutation<
      void,
      { params: GetLogisticParams; type?: LogisticReportType }
    >({
      query: ({ params }) => ({
        url: `/web/logistic/excel/`,
        params: filterObjectValues(params),
        cache: 'no-cache',
      }),
    }),
  }),
})
